import { DatePicker as AntDatePicker } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type React from "react";

const { RangePicker: AntRangePicker } = AntDatePicker;

dayjs.extend(customParseFormat);

const DATE_FORMATS = ["DDMMYYYY", "DD/MM/YYYY", "YYYY-MM-DD"];

export interface CustomRangePickerProps
  extends Omit<RangePickerProps, "format" | "onChange" | "value"> {
  format?: string | string[];
  value?: [string | null, string | null] | null;
  onChange?: (dateStrings: [string, string] | null) => void;
}

const RangePicker: React.FC<CustomRangePickerProps> = ({
  format = DATE_FORMATS,
  value,
  onChange,
  ...restProps
}) => {
  const getDayjsValues = (
    vals: [string | null, string | null] | null | undefined,
  ): [Dayjs | null, Dayjs | null] | null => {
    if (!vals) return null;

    const [startVal, endVal] = vals;

    const convertToDayjs = (val: string | null): Dayjs | null => {
      if (!val) return null;

      for (const fmt of Array.isArray(format) ? format : [format]) {
        const parsed = dayjs(val, fmt);
        if (parsed.isValid()) {
          return parsed;
        }
      }

      const parsed = dayjs(val);
      return parsed.isValid() ? parsed : null;
    };

    return [convertToDayjs(startVal), convertToDayjs(endVal)];
  };

  const handleRangeChange: RangePickerProps["onChange"] = (dates) => {
    if (dates?.[0] && dates?.[1]) {
      const formattedDates: [string, string] = [
        dates[0].format("DD/MM/YYYY"),
        dates[1].format("DD/MM/YYYY"),
      ];
      onChange?.(formattedDates);
    } else {
      onChange?.(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (/^\d{8}$/.test(value)) {
      try {
        const day = value.substring(0, 2);
        const month = value.substring(2, 4);
        const year = value.substring(4, 8);

        const date = dayjs(`${year}-${month}-${day}`);
        if (date.isValid()) {
          const formattedDate = `${day}/${month}/${year}`;
          e.target.value = formattedDate;
        }
      } catch (error) {
        console.error("Date parsing error:", error);
      }
    }
  };

  return (
    <AntRangePicker
      format={["DD/MM/YYYY", "DDMMYYYY"]}
      value={getDayjsValues(value)}
      onChange={handleRangeChange}
      renderExtraFooter={() => null}
      placeholder={["From", "To"]}
      {...restProps}
      onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
        handleInputChange(e);
      }}
    />
  );
};

export default RangePicker;
